/**
 * @bos/portal3 — CHANGES YOU ORDERED: the run's owner-order ledger, with the WITHDRAW door
 * (register B23).
 *
 * Every exact change a person ordered on this build — the data-model edits, the stage edits,
 * and the choices answers minted — is a typed row applied deterministically every time the
 * machine runs. Until B23 such an order was permanent; this screen shows each recorded order
 * with whether it is still IN FORCE, and offers "Withdraw this change" on the active ones.
 *
 * WORDING LAWS:
 *   - A WITHDRAWN ORDER IS HISTORY, NOT ERASURE. The row stays on screen, struck through,
 *     with when it was withdrawn — the record is append-only and the screen says what the
 *     record says.
 *   - THE COST IS SAID BEFORE THE CLICK: withdrawing re-derives the parts that were built on
 *     the change, at spend — the same sentence the edit doors state for ordering one.
 *   - A REFUSAL IS A SENTENCE, never a toast-shaped mystery.
 */
import { useState } from 'react';
import type { StageReviewStatus, RetractEditResult, RunOwnerEditsResult } from './hooks';
import { runErrorMessage } from './hooks';

interface OwnerEditsProps {
  readonly payload: RunOwnerEditsResult | null;
  readonly status: StageReviewStatus;
  /** A COMPLETED build's record is closed — the server refuses; the screen says it first.
   *  (Failed/stopped runs are resumable, and withdrawing before a resume is the use case.) */
  readonly completed: boolean;
  readonly onWithdraw: (index: number) => Promise<RetractEditResult>;
}

/** The result sentence for one withdrawal — exported for the co-located test. */
export function withdrawResultSentence(result: RetractEditResult): string {
  if (result.outcome === 'refused') return result.reason;
  return result.resumed
    ? 'Withdrawn — the machine is rebuilding without it now.'
    : 'Withdrawn. It no longer applies from the next time the machine runs.';
}

export function OwnerEdits({ payload, status, completed, onWithdraw }: OwnerEditsProps) {
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // Nothing ordered and nothing loading ⇒ no section at all: most runs never use a door,
  // and an empty card would only ask a person to wonder what they failed to order. A read
  // that FAILED says nothing rather than guessing (the ModelChoice law) — this section is
  // supplementary history, and a red box on every transient hiccup would cry wolf; the
  // WITHDRAW mutation's own failures still render below, because the person acted.
  if (status === 'ready' && (payload === null || payload.rows.length === 0)) return null;
  if (status === 'idle' || status === 'error') return null;

  async function withdraw(index: number) {
    setBusyIndex(index);
    setNote(null);
    setFailure(null);
    try {
      const result = await onWithdraw(index);
      if (result.outcome === 'refused') setFailure(result.reason);
      else setNote(withdrawResultSentence(result));
    } catch (err) {
      setFailure(runErrorMessage(err));
    } finally {
      setBusyIndex(null);
    }
  }

  return (
    <section className="rd" aria-label="Changes you ordered">
      <div className="rd__head">
        <h2 className="rd__title">Changes you ordered</h2>
        <p className="rd__lede">
          Exact changes you ordered on this build. Each applies every time the machine runs —
          withdrawing one takes it back out, and the parts built on it are re-derived.
        </p>
      </div>

      {status === 'loading' && (
        <p className="page__note" aria-busy="true">
          Reading this build’s record…
        </p>
      )}

      {failure !== null && (
        <p className="form__error" role="alert">
          {failure}
        </p>
      )}
      {note !== null && (
        <p className="form__success" role="status">
          {note}
        </p>
      )}

      {status === 'ready' && payload !== null && payload.rows.length > 0 && (
        <ul className="rd__list">
          {payload.rows.map((row) => (
            <li className="rd__row" key={row.index}>
              <div className="rd__row-head">
                <span
                  className="rd__name"
                  style={row.active ? undefined : { textDecoration: 'line-through', opacity: 0.6 }}
                >
                  {row.summary}
                </span>
                <span className="badge badge--kind">
                  {row.source === 'edit' ? 'your edit' : 'your answer'}
                </span>
              </div>
              {!row.active && (
                <p className="rd__empty">
                  Withdrawn{row.withdrawnAt !== null ? ` ${new Date(row.withdrawnAt).toLocaleString()}` : ''} — it
                  no longer applies.
                </p>
              )}
              {row.active && !completed && (
                <div className="form__actions">
                  <button
                    className="btn btn--quiet btn--sm"
                    type="button"
                    disabled={busyIndex !== null}
                    onClick={() => void withdraw(row.index)}
                  >
                    {busyIndex === row.index ? 'Withdrawing…' : 'Withdraw this change'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {completed && status === 'ready' && (
        <p className="rd__empty">
          This build is finished, so its record is closed — nothing can be withdrawn any more.
        </p>
      )}
    </section>
  );
}
